import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import {
  Phone,
  Tag,
  AlignLeft,
  X,
  Save,
  Loader2,
} from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';

// ── Design tokens (matches AddEditLeadModal / Leads) ─────────────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types ─────────────────────────────────────────────────────────────────────
export interface CallLogNote {
  text: string;
  type: 'note' | 'call_log';
  outcome: string | null;
  extra_note: string | null;
  event_date: string | null;
  created_at: string | null;
  added_by_email: string | null;
  added_by_role: string | null;
}

export interface CallLogLead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  source: string | null;
  status: string | null;
  notes: string | null;
  notes_list: CallLogNote[];
  city: string | null;
  created_at: string | null;
  assigned_to: { id: string; email: string; role?: string | null; agency_id?: string | null } | null;
  assigned_by: { id: string; email: string; role?: string | null } | null;
  assigned_at: string | null;
}

interface LogCallModalProps {
  visible: boolean;
  onHide: () => void;
  leadId: string;
  leadName: string;
  onSaved: (updatedLead: CallLogLead) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CALL_OUTCOMES = [
  { label: '✅ Answered', value: 'Answered' },
  { label: '📵 Not Answered', value: 'Not Answered' },
  { label: '🚫 Not Attempted', value: 'Not Attempted' },
  { label: '🔁 Call Back Later', value: 'Call Back Later' },
  { label: '❌ Not Interested', value: 'Not Interested' },
  { label: '🎯 Interested', value: 'Interested' },
  { label: '💬 Left Voicemail', value: 'Left Voicemail' },
];

const OUTCOME_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  Answered: { bg: '#e8f8e8', color: '#2e7d32', border: 'rgba(46,125,50,0.25)' },
  'Not Answered': { bg: '#fff3e0', color: '#e65100', border: 'rgba(230,81,0,0.25)' },
  'Not Attempted': { bg: '#f3e5f5', color: '#7b1fa2', border: 'rgba(123,31,162,0.25)' },
  'Call Back Later': { bg: '#e3f2fd', color: '#1565c0', border: 'rgba(21,101,192,0.25)' },
  'Not Interested': { bg: '#fce4ec', color: '#c62828', border: 'rgba(198,40,40,0.25)' },
  Interested: { bg: '#e8f5e9', color: '#1b5e20', border: 'rgba(27,94,32,0.25)' },
  'Left Voicemail': { bg: '#f3f4f6', color: '#374151', border: 'rgba(55,65,81,0.2)' },
};

const getOutcomeStyle = (o: string | null) =>
  OUTCOME_COLORS[o ?? ''] ?? { bg: '#f3f7f6', color: '#4a6865', border: 'rgba(22,112,106,0.2)' };

// ── Small building blocks (matches AddEditLeadModal) ──────────────────────────
const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span
      style={{
        color: C.teal,
        fontWeight: 700,
        fontSize: '0.71rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: C.teal,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '5.5px',
};

// ─────────────────────────────────────────────────────────────────────────────

const LogCallModal: React.FC<LogCallModalProps> = ({
  visible,
  onHide,
  leadId,
  leadName,
  onSaved,
}) => {
  const toast = useRef<Toast>(null);

  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [extraNote, setExtraNote] = useState('');
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedOutcome(null);
      setExtraNote('');
      setEventDate(null);
      setIsClosing(false);
    }
  }, [visible]);

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onHide();
      setIsClosing(false);
    }, 300);
  };

  const showError = (detail: string) =>
    toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 4000 });

  const handleSubmit = async () => {
    if (!selectedOutcome) {
      showError('Please select a call outcome');
      return;
    }

    setLoading(true);
    try {
      const label =
        CALL_OUTCOMES.find((o) => o.value === selectedOutcome)?.label ?? selectedOutcome;
      const text = extraNote.trim()
        ? `📞 Call Log — ${label}\n${extraNote.trim()}`
        : `📞 Call Log — ${label}`;

      const form = new FormData();
      form.append('type', 'call_log');
      form.append('note', text);
      form.append('outcome', selectedOutcome);
      form.append('extra_note', extraNote.trim());
      if (eventDate) form.append('event_date', eventDate.toISOString().split('T')[0]);

      const res = await apiClient.post(`/v1/leads/${leadId}/notes`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Call log saved successfully!',
          life: 3000,
        });
        const saved: CallLogLead = res.data.data;
        setTimeout(() => {
          onSaved(saved);
          handleClose();
        }, 250);
      } else {
        showError(res.data.error || 'Failed to save call log');
      }
    } catch (e: any) {
      showError(e.response?.data?.error || e.response?.data?.message || 'Failed to save call log');
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          ...iconBox(44),
          borderRadius: '12px',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Phone size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: '1.01rem',
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            marginBottom: '3px',
          }}
        >
          Log Call
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          Record outcome for <strong style={{ color: C.teal }}>{leadName}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className={`logcall-dialog ${isClosing ? 'logcall-dialog-closing' : ''}`}
        maskClassName={`logcall-mask ${isClosing ? 'logcall-mask-closing' : ''}`}
        style={{ width: '90vw', maxWidth: '520px' }}
        modal
        dismissableMask={!loading}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .logcall-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: logcallMaskIn 0.3s ease; }
          .logcall-mask-closing { animation: logcallMaskOut 0.3s ease forwards !important; }
          .logcall-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: logcallDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
          .logcall-dialog-closing.p-dialog { animation: logcallDlgOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards !important; }
          .logcall-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .logcall-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .logcall-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 72vh; overflow-y: auto; }
          @keyframes logcallMaskIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes logcallMaskOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes logcallDlgIn  { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes logcallDlgOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92) translateY(16px); } }
          .lcm-oc:hover { transform: translateY(-1px); }
          .lcm-spin { animation: lcm-spin 0.9s linear infinite; }
          @keyframes lcm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            opacity: loading ? 0.85 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {/* ── Call Outcome ─────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<Tag size={14} />} title="Call Outcome" />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6.5px' }}>
                {CALL_OUTCOMES.map((o) => {
                  const os = getOutcomeStyle(o.value);
                  const sel = selectedOutcome === o.value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => setSelectedOutcome(o.value)}
                      className="lcm-oc"
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: `1.5px solid ${sel ? os.border : G.line}`,
                        background: sel ? os.bg : C.white,
                        color: sel ? os.color : C.sub,
                        fontSize: '0.72rem',
                        fontWeight: sel ? 700 : 500,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        boxShadow: sel ? `0 2px 8px ${os.border}` : 'none',
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {!selectedOutcome && (
                <p
                  style={{
                    margin: '10px 0 0',
                    fontSize: '0.68rem',
                    color: C.muted,
                    fontWeight: 500,
                  }}
                >
                  Select the outcome of this call
                </p>
              )}
            </div>
          </div>

          {/* ── Details ──────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<AlignLeft size={14} />} title="Details" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                {/* Date */}
                <GlobalDateField
                  label="Call Date"
                  value={eventDate}
                  onChange={(v) => setEventDate((v as Date) ?? null)}
                  placeholder="dd/mm/yyyy"
                  disabled={loading}
                  dateFormat="dd/mm/yy"
                  showButtonBar
                />

                {/* Extra note */}
                <div>
                  <label style={FIELD_LABEL}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <AlignLeft size={11} /> Notes
                      <span style={{ fontWeight: 500, color: C.muted, textTransform: 'none' }}>
                        (optional)
                      </span>
                    </span>
                  </label>
                  <div
                    style={{
                      display: 'flex',
                      gap: '7.4px',
                      padding: '9px 11px',
                      background: loading ? '#f9fafb' : C.white,
                      border: `1px solid ${G.line}`,
                      borderRadius: '8.3px',
                    }}
                  >
                    <span
                      style={{
                        color: C.muted,
                        display: 'flex',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    >
                      <AlignLeft size={14} />
                    </span>
                    <InputTextarea
                      value={extraNote}
                      disabled={loading}
                      onChange={(e) => setExtraNote(e.target.value)}
                      placeholder="Any additional notes about the call…"
                      rows={3}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        padding: 0,
                        fontSize: '0.79rem',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '7.4px',
              marginTop: '2px',
            }}
          >
            <GlobalButton
              label="Cancel"
              variant="secondary"
              icon={X}
              onClick={handleClose}
              disabled={loading}
            />
            <GlobalButton
              label={loading ? 'SAVING...' : 'SAVE LOG'}
              variant="primary"
              icon={loading ? Loader2 : Save}
              onClick={handleSubmit}
              disabled={loading || !selectedOutcome}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default LogCallModal;